<template>
  <div class="px-3 py-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="mb-0">Passes</h2>
      <b-button class="btn btn-secondary" @click="$router.push('/pass/create')">
        Create Pass
      </b-button>
    </div>

    <div class="d-flex flex-wrap justify-content-start mb-4 gap-3">
      <!-- Search Card -->
      <b-card class="flex-fill" style="min-width: 300px; max-width: 500px;">
        <h5>Search</h5>
        <b-form @submit.stop.prevent>
          <b-form-group>
            <b-form-input id="search_query" v-model="query" size="md" type="text" class="form-control form-control-lg"
              placeholder="Search by barcode or RFID" v-on:input="searchQuery"></b-form-input>
          </b-form-group>
        </b-form>
      </b-card>

      <!-- Filters Card -->
      <b-card class="flex-fill" style="min-width: 300px; max-width: 500px;">
        <h5>Filters</h5>
        <b-form-group class="mt-2">
          <b-form-checkbox v-model="show_all_seasons" @change="toggledShowAllSeasons">All Seasons</b-form-checkbox>
          <b-form-checkbox v-model="show_disabled_passes" @change="toggledShowDisabledPasses">Disabled passes</b-form-checkbox>
          <b-form-checkbox v-model="show_expired_passes" @change="toggledShowExpiredPasses">Expired passes</b-form-checkbox>
          <b-form-checkbox v-model="show_passes_not_printed">Passes Not Printed</b-form-checkbox>
          <b-form-checkbox v-model="show_passes_not_linked">Passes Not Linked</b-form-checkbox>
        </b-form-group>
      </b-card>
    </div>

    <v-server-table ref="passes_table" :columns="columns" :options="options" @loading="onLoading" @loaded="onLoaded">
      <template #billable="{ row }">
        <b-form-checkbox v-model="row.billable" disabled></b-form-checkbox>
      </template>
      <template #disabled="{ row }">
        <b-form-checkbox v-model="row.disabled" disabled></b-form-checkbox>
      </template>
      <template #actions="{ row }">
        <div class="actions">
          <b-button size="sm" variant="outline-secondary" @click="$router.push(`/passes/${row.pass_id}/edit`)">
            <icon name="pencil" class="mr-1" /> Edit
          </b-button>
        </div>
      </template>
      <template #uploaded="{ row }">
        <div class="d-flex justify-content-center align-items-center">
          <icon v-if="row.uploaded" name="check-circle" class="text-success" />
          <span v-else class="text-danger">x</span>
        </div>
      </template>
      <template #afterBody>
        <div v-show="loading">
          <div class="overlay-loader"></div>
          <icon scale="1" name="spinner" spin class="clip-loader" />
        </div>
      </template>
    </v-server-table>

    <b-tabs class="mt-5" v-show="!loading">
      <b-tab title="CSV Import" active>
        <b-container class="mt-4">
          <h5>Import Passholders via CSV</h5>
          <b-row class="align-items-center">
            <b-col sm="8">
              <label for="csv-upload" class="upload-label">
                <input id="csv-upload" type="file" accept=".csv" @change="uploadFile" class="hidden" />
              </label>
            </b-col>
            <b-col sm="4" class="text-right">
              <icon scale="1" height="30px" width="30px" name="spinner" v-show="upload_in_progress" spin />
              <icon scale="1" height="30px" width="30px" name="check-circle" class="text-success" v-show="upload_successful && !upload_in_progress" />
              <icon scale="1" height="30px" width="30px" name="x-circle" class="text-danger" v-show="upload_failed && !upload_in_progress" />
            </b-col>
          </b-row>

          <b-row class="mt-3" v-show="import_result.total_rows > 0">
            <b-col>
              <h6>Import Summary</h6>
              <b-alert show variant="info">
                Imported {{ import_result.total_rows }} row(s):
                <strong class="text-success">{{ import_result.successes }} succeeded</strong>,
                <strong class="text-danger">{{ import_result.failures }} failed</strong>.
              </b-alert>

              <b-progress :max="import_result.total_rows" height="30px" animated>
                <b-progress-bar :value="import_result.successes" variant="success">
                  {{ import_result.successes }} Succeeded
                </b-progress-bar>
                <b-progress-bar :value="import_result.failures" variant="danger">
                  {{ import_result.failures }} Failed
                </b-progress-bar>
              </b-progress>

              <div v-if="import_result.failures_details && import_result.failures_details.length > 0" class="mt-3">
                <h6>Failed Records</h6>
                <b-table small bordered :items="import_result.failures_details" :fields="Object.keys(import_result.failures_details[0] || {})" responsive />
              </div>
            </b-col>
          </b-row>

          <b-alert class="mt-3" :show="show_failed_import" variant="danger">
            An unexpected error occurred during import. Please contact support.
          </b-alert>
        </b-container>
      </b-tab>

      <b-tab title="CSV Export">
        <div class="mt-4">
          <b-button @click="saveFile">
            Export
            <icon v-show="export_in_progress" scale="1" name="spinner" spin class="ml-2" />
          </b-button>
          <b-alert class="mt-3" :show="show_failed_export" variant="danger">
            An error occurred during export. Please contact support.
          </b-alert>
        </div>
      </b-tab>
    </b-tabs>

    <b-modal id="print-modal" title="Print Pass" hide-footer ref="print-modal">
      <PrintModalContents v-if="currentPass" :currentPass="currentPass" ref="print-modal-contents" />
      <b-button class="mt-2 print-button" variant="outline-primary" @click="print">Print</b-button>
      <div id="action-buttons" class="mt-3">
        <b-button variant="outline-secondary" @click="$refs['print-modal'].hide()">Close</b-button>
        <b-button variant="outline-primary" @click="confirmPrint">RFID Scan</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from "../api";
import moment from "moment";
import { BIconPencil } from "bootstrap-vue";
import PrintModalContents from "./PrintModalContents.vue";
moment.locale("en-nz");

var current_query = {};

export default {
  name: "Passes",
  components: {
    BIconPencil,
    PrintModalContents,
  },
  data() {
    return {
      columns: [
        "barcode", "billable", "disabled", "pass_holder_name", "pass_holder_notes",
        "rfid", "type_name", "notes", "last_check_in_time", "last_check_in_org_name",
        "expiry_date", "actions", "order_number", "organisation_name", "uploaded"
      ],
      data: [],
      options: {
        headings: {
          barcode: "Barcode",
          billable: "Billable",
          disabled: "Disabled",
          notes: "Notes",
          rfid: "RFID",
          type_name: "Pass Type",
          expiry_date: "Expiry",
          actions: "Actions",
          order_number: "Order Number",
          organisation_name: "Organisation Name",
          uploaded: "Uploaded"
        },
        perPage: 10,
        sortable: [],
        pagination: { dropdown: false },
        filterable: ["rfid", "barcode"],
        requestFunction: (data) => {
          data.show_disabled_passes = !this.show_disabled_passes;

          if (this.show_all_seasons) data.show_all_seasons = true;
          if (this.show_passes_not_printed) data.show_passes_not_printed = true;
          if (this.show_passes_not_linked) data.show_passes_not_linked = true;
          if (api.userIsAdmin()) data.showAll = true;

          current_query = data;
          return api.getAllPasses(data).catch(e => this.dispatch("error", e));
        },
        responseAdapter(response) {
          response.data.ret_msg.map((row) => {
            row.last_check_in_time = row.last_check_in_time
              ? moment(row.last_check_in_time).format("lll")
              : null;
            const hasSeasonPassOption = !!row.options.find(
              (option) => option.optionName === 'isSeasonPass'
            );
            row.expiry_date = row.expiry_date && !hasSeasonPassOption
              ? moment(row.expiry_date).format("ll")
              : null;
            row.uploaded = row.image != null;
          });
          return {
            data: response.data.ret_msg,
            count: response.data.ret_msg.length > 0
              ? response.data.ret_msg[0].full_count
              : 0
          };
        },
      },
      query: null,
      file: null,
      upload_in_progress: false,
      upload_successful: false,
      upload_failed: false,
      export_in_progress: false,
      show_failed_export: false,
      import_result: {
        total_rows: 0,
        successes: 0,
        failures: 0,
        failures_details: [],
      },
      show_failed_import: false,
      enabled: true,
      uniqueKey: "pass_id",
      events: {},
      loading: false,
      show_expired_passes: localStorage.getItem("show_expired_passes") || true,
      show_disabled_passes: localStorage.getItem("show_disabled_passes") || true,
      show_all_seasons: api.getUserOrganisationId() === '308' || true,
      show_passes_not_printed: false,
      show_passes_not_linked: false,
      currentPass: null,
      exportCurrentSeason: true,
      isAdmin: api.userIsAdmin()
    };
  },
  methods: {
    onLoading() { this.loading = true; },
    onLoaded() { this.loading = false; },
    toggledShowAllSeasons() {
      localStorage.setItem("show_all_seasons", this.show_all_seasons);
      this.$refs.passes_table.refresh();
    },
    toggledShowDisabledPasses() {
      localStorage.setItem("show_disabled_passes", this.show_disabled_passes);
      this.$refs.passes_table.refresh();
    },
    toggledShowExpiredPasses() {
      localStorage.setItem("show_expired_passes", this.show_expired_passes);
      this.$refs.passes_table.refresh();
    },
    csvToJson(csvText) {
      const lines = csvText.trim().split("\n");
      const headers = lines[0].split(",").map(h => h.trim());
      return lines.slice(1).map(line => {
        const values = line.split(",").map(v => v.trim());
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index];
          return obj;
        }, {});
      });
    },
    async uploadFile(event) {
      try {
        this.upload_in_progress = true;
        this.upload_successful = false;
        this.upload_failed = false;

        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const text = e.target.result;
            const jsonData = this.csvToJson(text);
            const result = await api.postCsvData(jsonData);
            this.import_result = result.data;
            this.upload_successful = true;
          } catch (err) {
            console.error(err);
            this.upload_failed = true;
            this.show_failed_import = true;
          } finally {
            this.upload_in_progress = false;
          }
        };
        reader.readAsText(file);
      } catch (ex) {
        console.log(ex);
        this.upload_failed = true;
        this.upload_in_progress = false;
        this.show_failed_import = true;
      }
    },
    saveFile() {
      this.export_in_progress = true;
      api.getAllPassesAsCsv(current_query)
        .then(result => {
          const blob = new Blob([result.data], { type: "text/plain" });
          const a = document.createElement("a");
          a.download = `chillstudio_passholder_data_${new Date().toISOString().replace(/(:|\.)/g, "_")}.csv`;
          a.href = URL.createObjectURL(blob);
          a.click();
          this.export_in_progress = false;
        })
        .catch(error => {
          console.log(error);
          this.show_failed_export = true;
          this.export_in_progress = false;
        });
    },
    searchQuery() {
      this.$refs.passes_table.setFilter(this.query);
    },
    showPrintPass(currentPass) {
      this.currentPass = currentPass;
      this.$bvModal.show("print-modal");
    },
    retryNotReceivedEmail() {
      api.retryNotReceivedEmail();
    },
    print() {
      window.print();
    },
    async confirmPrint() {
      const passId = this.currentPass.pass_id;
      await api.setPassToPrinted(passId);
      this.$router.push(`/passes/${passId}/edit`);
    },
  },
  watch: {
    show_all_seasons() { this.$refs.passes_table.refresh(); },
    show_passes_not_printed() { this.$refs.passes_table.refresh(); },
    show_passes_not_linked() { this.$refs.passes_table.refresh(); },
  },
};
</script>

<style>
table {
  width: 100%;
  position: relative;
}
.overlay-loader {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  opacity: 0.8;
  filter: blur(5px);
}
.clip-loader {
  position: absolute;
  left: 50%;
  right: 50%;
  bottom: 60%;
  top: 40%;
  width: 60px;
  height: 60px;
}
.VueTables__search {
  display: none;
}
.pass-edit, .print-icon {
  color: #6c757d;
  cursor: pointer;
  font-size: 20px;
}
.actions {
  display: flex;
}
.action {
  margin-right: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/Passes.vue